"use client";

import { useEffect, useState } from "react";
import {
  CreditCard,
  TrendingUp,
  DollarSign,
  Clock,
  Download,
  RefreshCw,
  Eye,
  Mail,
  CircleDollarSign,
  type LucideIcon,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Stat,
  List,
  ListItem,
  Segmented,
  Spinner,
  Empty,
  Dialog,
  Button,
  useToast,
} from "@/components/ui";

import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  createdAt: string;
  user: { id: string; name: string | null; email: string };
}

interface Stats {
  totalRevenue: number;
  monthRevenue: number;
  activeSubscriptions: number;
}

const STATUS_OPTIONS = ["ALL", "COMPLETED", "PENDING", "FAILED", "REFUNDED"] as const;
const PROVIDER_OPTIONS = ["ALL", "app_store", "play_store", "stripe"] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];
type ProviderFilter = (typeof PROVIDER_OPTIONS)[number];

const STATUS_BADGE_VARIANT: Record<string, "success" | "warning" | "error" | "neutral"> = {
  COMPLETED: "success",
  PENDING:   "warning",
  FAILED:    "error",
  REFUNDED:  "neutral",
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StatusFilter>("ALL");
  const [provider, setProvider] = useState<ProviderFilter>("ALL");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [clawbackCredits, setClawbackCredits] = useState(false);
  const [exportPeriod, setExportPeriod] = useState<"all" | "30days" | "90days" | "12months">("all");
  const [exporting, setExporting] = useState(false);

  const { showToast } = useToast();

  const fetchPayments = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await adminApi.get<{
        success: boolean;
        payments: Payment[];
        total: number;
        pages: number;
        stats: Stats;
      }>(`/admin/payments?page=${pageNum}&status=${status}&provider=${provider}`);
      if (res.success) {
        setPayments(res.payments);
        setTotal(res.total);
        setTotalPages(res.pages);
        setStats(res.stats);
        setPage(pageNum);
      }
    } catch (e: any) {
      showToast({ title: "Failed to load payments", description: e?.message, variant: "error" });
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, provider]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchPayments(newPage);
  };

  const handleRefund = async () => {
    if (!selectedPayment) return;
    setActionLoading(selectedPayment.id);
    try {
      await adminApi.post(`/admin/payments/${selectedPayment.id}/refund`, { clawbackCredits });
      showToast({ title: "Refund processed", variant: "success" });
      setShowRefundModal(false);
      setSelectedPayment(null);
      setClawbackCredits(false);
      fetchPayments(page);
    } catch (e: any) {
      showToast({ title: "Failed to process refund", description: e?.message, variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ status, provider, period: exportPeriod });
      const response = await fetch(`/api/admin/payments/export?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("merryflix_admin_token") || ""}`,
        },
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payments-${exportPeriod}-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast({ title: "Export downloaded", variant: "success" });
    } catch (e: any) {
      showToast({ title: "Export failed", description: e?.message, variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const from = payments.length === 0 ? 0 : (page - 1) * 25 + 1;
  const to = (page - 1) * 25 + payments.length;

  const statusOptions = STATUS_OPTIONS.map((s) => ({ value: s, label: s === "ALL" ? "All" : s }));
  const providerOptions = PROVIDER_OPTIONS.map((p) => ({
    value: p,
    label: p === "ALL" ? "All" : p.replace(/_/g, " "),
  }));

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="Payments"
        subtitle={
          stats
            ? `${total.toLocaleString()} transactions · $${(stats.totalRevenue / 100).toLocaleString()} lifetime revenue`
            : `${total.toLocaleString()} transactions`
        }
        actions={
          <div className="flex items-center gap-2">
            <select
              value={exportPeriod}
              onChange={(e) => setExportPeriod(e.target.value as typeof exportPeriod)}
              className="h-9 rounded-lg border border-line bg-abyss/40 px-3 text-[13px] text-cream outline-none transition focus:border-rose/40"
              aria-label="Export period"
            >
              <option value="all">All time</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
              <option value="12months">Last 12 months</option>
            </select>
            <Button variant="secondary" size="md" onClick={handleExport} disabled={exporting}>
              {exporting ? <Spinner className="!h-3.5 !w-3.5" /> : <Download className="h-3.5 w-3.5" />}
              Export CSV
            </Button>
            <Button variant="secondary" size="md" iconOnly onClick={() => fetchPayments(page)} disabled={loading} title="Refresh">
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            </Button>
          </div>
        }
      />

      {/* Stats */}
      {stats && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Total Revenue"
            value={`$${(stats.totalRevenue / 100).toLocaleString()}`}
            tint="emerald"
            icon={<DollarSign className="h-4 w-4" />}
          />
          <Stat
            label="This Month"
            value={`$${(stats.monthRevenue / 100).toLocaleString()}`}
            tint="blue"
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <Stat
            label="Active Subscriptions"
            value={stats.activeSubscriptions.toLocaleString()}
            tint="purple"
            icon={<CreditCard className="h-4 w-4" />}
          />
          <Stat
            label="Total Transactions"
            value={total.toLocaleString()}
            tint="gold"
            icon={<Clock className="h-4 w-4" />}
          />
        </div>
      )}

      {/* Toolbar */}
      <Card className="!p-3 mt-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="hidden text-[10px] font-bold uppercase tracking-widest text-dim sm:block sm:w-16">
            Status
          </span>
          <Segmented<StatusFilter>
            value={status}
            options={statusOptions}
            onChange={setStatus}
          />
          <span className="hidden text-[10px] font-bold uppercase tracking-widest text-dim sm:block sm:w-16">
            Provider
          </span>
          <Segmented<ProviderFilter>
            value={provider}
            options={providerOptions}
            onChange={setProvider}
          />
          <div className="ml-auto text-[12px] text-dim">
            {total.toLocaleString()} total
          </div>
        </div>
      </Card>

      {/* Payments list */}
      <Card className="!p-0 mt-4 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner className="!h-6 !w-6" />
          </div>
        ) : payments.length === 0 ? (
          <Empty
            icon={<CircleDollarSign className="h-5 w-5" />}
            title="No payments found"
            message="Try a different status or provider filter."
          />
        ) : (
          <List className="!rounded-none">
            {payments.map((p, i) => (
              <PaymentRow
                key={p.id}
                payment={p}
                noSeparator={i === 0}
                processing={actionLoading === p.id}
                onView={() => setSelectedPayment(p)}
                onRefund={() => {
                  setSelectedPayment(p);
                  setClawbackCredits(false);
                  setShowRefundModal(true);
                }}
              />
            ))}
          </List>
        )}
      </Card>

      {/* Pagination */}
      {payments.length > 0 && (
        <div className="mt-4 flex items-center justify-between text-[12px] text-dim">
          <span>
            {from}–{to} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Payment Detail modal */}
      <Dialog
        open={!!selectedPayment && !showRefundModal}
        onClose={() => setSelectedPayment(null)}
        title="Payment details"
        width={460}
      >
        {selectedPayment && (
          <>
            <div className="space-y-2.5 text-[13px]">
              <DetailRow label="Payment ID" value={selectedPayment.id} mono />
              <DetailRow label="User" value={selectedPayment.user.name || selectedPayment.user.email} />
              <DetailRow label="Email" value={selectedPayment.user.email} />
              <DetailRow label="Provider" value={selectedPayment.provider.toUpperCase()} />
              <DetailRow
                label="Amount"
                value={(selectedPayment.amount / 100).toLocaleString("en-US", {
                  style: "currency",
                  currency: selectedPayment.currency.toUpperCase(),
                })}
              />
              <div className="flex items-center justify-between border-t border-line/60 pt-2.5">
                <span className="text-dim">Status</span>
                <StatusBadge variant={STATUS_BADGE_VARIANT[selectedPayment.status] ?? "neutral"}>
                  {selectedPayment.status}
                </StatusBadge>
              </div>
              <DetailRow label="Date" value={new Date(selectedPayment.createdAt).toLocaleString()} />
            </div>
            <div className="dialog-actions">
              {selectedPayment.status === "COMPLETED" && (
                <Button variant="destructive" size="md" onClick={() => setShowRefundModal(true)}>
                  Refund
                </Button>
              )}
              <Button variant="secondary" size="md" onClick={() => setSelectedPayment(null)}>
                Close
              </Button>
            </div>
          </>
        )}
      </Dialog>

      {/* Refund confirmation modal */}
      <Dialog
        open={showRefundModal && !!selectedPayment}
        onClose={() => {
          setShowRefundModal(false);
          setSelectedPayment(null);
          setClawbackCredits(false);
        }}
        title="Confirm refund"
        message="This action cannot be undone. The customer will be notified."
        width={460}
      >
        {selectedPayment && (
          <>
            <div className="rounded-xl border border-line bg-abyss/40 p-4">
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-dim">Amount</span>
                <span className="font-semibold">
                  {(selectedPayment.amount / 100).toLocaleString("en-US", {
                    style: "currency",
                    currency: selectedPayment.currency.toUpperCase(),
                  })}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-[13px]">
                <span className="text-dim">User</span>
                <span>{selectedPayment.user.email}</span>
              </div>
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-amber-300/30 bg-amber-500/8 p-3">
              <input
                type="checkbox"
                checked={clawbackCredits}
                onChange={(e) => setClawbackCredits(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded text-amber-500 focus:ring-amber-400"
              />
              <div className="text-[12.5px] leading-relaxed">
                <span className="font-semibold text-amber-200">Clawback credits from this user's wallet</span>
                <p className="mt-1 text-[11.5px] text-amber-200/80">
                  Force-debits the wallet by the credits granted for this payment.
                  Credits may already be spent — the wallet can go negative.
                  Leave OFF unless the user is abusing the system.
                </p>
              </div>
            </label>

            <div className="dialog-actions">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setShowRefundModal(false);
                  setSelectedPayment(null);
                  setClawbackCredits(false);
                }}
              >
                Cancel
              </Button>
              <Button variant="destructive" size="md" onClick={handleRefund} disabled={!!actionLoading}>
                {actionLoading ? <Spinner className="!h-3.5 !w-3.5" /> : null}
                Confirm refund
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}

/* ─── Row renderer ─────────────────────────────────────────────────────── */

function PaymentRow({
  payment, noSeparator, processing, onView, onRefund,
}: {
  payment: Payment;
  noSeparator: boolean;
  processing: boolean;
  onView: () => void;
  onRefund: () => void;
}) {
  const statusVariant = STATUS_BADGE_VARIANT[payment.status] ?? "neutral";
  const LeadingIcon: LucideIcon = payment.provider === "stripe" ? CircleDollarSign : CreditCard;
  const leadingTint = payment.provider === "stripe" ? "purple" : payment.provider === "app_store" ? "blue" : payment.provider === "play_store" ? "green" : "orange";
  const initial = ((payment.user.name || payment.user.email) || "?").charAt(0).toUpperCase();

  return (
    <ListItem
      noSeparator={noSeparator}
      leading={<LeadingIcon className="h-4 w-4" />}
      leadingTint={leadingTint}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">
            {(payment.amount / 100).toLocaleString("en-US", {
              style: "currency",
              currency: payment.currency.toUpperCase(),
            })}
          </span>
          <StatusBadge variant={statusVariant}>{payment.status}</StatusBadge>
        </span>
      }
      subtitle={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <span className="font-semibold">{initial}</span>
            {payment.user.name || payment.user.email}
          </span>
          <span className="inline-flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {payment.user.email}
          </span>
          <span className="uppercase">{payment.provider.replace(/_/g, " ")}</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(payment.createdAt).toLocaleString()}
          </span>
        </span>
      }
      trailing={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconOnly onClick={onView} title="View details">
            <Eye className="h-3.5 w-3.5" />
          </Button>
          {payment.status === "COMPLETED" && (
            <Button variant="destructive" size="sm" iconOnly disabled={processing} onClick={onRefund} title="Refund">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      }
    />
  );
}

function DetailRow({
  label, value, mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between border-t border-line/40 py-1.5 first:border-t-0 first:pt-0">
      <span className="text-dim">{label}</span>
      <span className={cn(mono && "font-mono")}>{value}</span>
    </div>
  );
}

function StatusBadge({
  variant, children,
}: {
  variant: "success" | "warning" | "error" | "neutral";
  children: React.ReactNode;
}) {
  const tintMap: Record<string, string> = {
    success: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
    warning: "bg-amber-500/15 text-amber-200 ring-amber-400/30",
    error:   "bg-red-500/15 text-red-300 ring-red-400/30",
    neutral: "bg-white/8 text-white/70 ring-white/15",
  };
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset",
      tintMap[variant],
    )}>
      {children}
    </span>
  );
}